import { Injectable } from '@angular/core';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { jwtDecode } from 'jwt-decode';
import { ExtendedIdTokenClaims } from '../_models/extendedIdTokenClaims';
import { environment } from '../../environments/environment';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // User state observables
  private isLoggedIn = new BehaviorSubject<boolean>(false);
  private displayName = new BehaviorSubject<string>('Anonymous User');
  private userName = new BehaviorSubject<string>('Anonymous User');
  private isAdminState = new BehaviorSubject<boolean>(false);
  private isStudState = new BehaviorSubject<boolean>(false);
  private isStaffState = new BehaviorSubject<boolean>(false);
  private languageState = new BehaviorSubject<string>('de');

  private idTokenClaims: ExtendedIdTokenClaims = { xms_pl: 'de' };

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
  ) {
    this.setupMsalEventListeners();
  }

  // Public observables for user state
  get isLoggedIn$(): Observable<boolean> {
    return this.isLoggedIn.asObservable();
  }

  get displayName$(): Observable<string> {
    return this.displayName.asObservable();
  }

  get userName$(): Observable<string> {
    return this.userName.asObservable();
  }

  get tokenClaims(): ExtendedIdTokenClaims {
    return this.idTokenClaims;
  }

  get isAdmin$(): Observable<boolean> {
    return this.isAdminState.asObservable();
  }

  get isStaff$(): Observable<boolean> {
    return this.isStaffState.asObservable();
  }

  get isStud$(): Observable<any> {
    return this.isStudState.asObservable();
  }

  get language$(): Observable<string> {
    return this.languageState.asObservable();
  }

  async initialize(): Promise<void> {
    await firstValueFrom(this.msalService.initialize());

    const result = await firstValueFrom(
      this.msalService.handleRedirectObservable(),
    );

    if (result) {
      this.msalService.instance.setActiveAccount(result.account);
      this.updateMsalUserState();

      if (
        window.location.pathname === '/' ||
        window.location.href.endsWith(environment.appUrl) ||
        window.location.href.endsWith(`${environment.appUrl}/`)
      ) {
        this.router.navigate(['/create-resources']);
      }
    }
    await this.initializeUserState();
  }

  private async initializeUserState(): Promise<void> {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.updateMsalUserState();
      return;
    }

    await this.msalService.instance.loginRedirect({
      scopes: ['openid', 'profile', 'offline_access'],
    });
  }

  // Setup MSAL Listener
  private setupMsalEventListeners(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.updateMsalUserState();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE,
        ),
      )
      .subscribe(() => {
        this.resetUserState();
      });
  }

  private updateMsalUserState(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      // Update user state
      this.isLoggedIn.next(true);
      this.displayName.next(activeAccount.name ?? 'Anonymous User');
      this.userName.next(activeAccount.username ?? 'Anonymous User');

      // Update language
      if (activeAccount.idToken)
        this.idTokenClaims = jwtDecode(
          activeAccount.idToken,
        ) as ExtendedIdTokenClaims;
      if (this.idTokenClaims.xms_pl)
        this.languageState.next(this.idTokenClaims.xms_pl);

      // Update roles
      this.updateRoleState();
    } else {
      this.resetUserState();
    }
  }

  private resetUserState(): void {
    this.isLoggedIn.next(false);
    this.displayName.next('Anonymous User');
    this.userName.next('Anonymous User');
    this.languageState.next('de');
    this.isAdminState.next(false);
    this.isStaffState.next(false);
    this.isStudState.next(false);
  }

  getDisplayName(): string {
    const account = this.msalService.instance.getActiveAccount();
    return account ? account.name! : 'Anonymous User';
  }

  getUsername(): string {
    const account = this.msalService.instance.getActiveAccount();
    return account ? account.username! : 'Anonymous User';
  }

  isAdmin(): boolean {
    return this.userHasRole('BernieRDM.Admin');
  }

  isStaff(): boolean {
    return this.userHasRole('BernieRDM.Staff');
  }

  isStud(): boolean {
    return this.userHasRole('BernieRDM.Stud');
  }

  private updateRoleState(): void {
    this.isAdminState.next(this.isAdmin());
    this.isStaffState.next(this.isStaff());
    this.isStudState.next(this.isStud());
  }

  private userHasRole(role: string): boolean {
    const claims = this.getMsalUserClaims();
    if (claims && claims.roles) return claims.roles.includes(role);

    return false;
  }

  private getMsalUserClaims(): any {
    const account = this.msalService.instance.getActiveAccount();
    if (!account || !account.idToken) return null;

    return jwtDecode(account.idToken) as any;
  }

  async login(): Promise<void> {
    await this.msalService.instance.loginRedirect({
      scopes: ['openid', 'profile', 'offline_access'],
    });
  }

  logout(): void {
    this.resetUserState();
    this.msalService.logout();
  }
}
